import { computeCompleteness } from './entityCoverage';
import type { MeasureEdge } from './entityCoverage';
import type { PlanContainment, UndecidedReason } from './planContainment';

/**
 * The three rules about a complex measure that can only be decided by looking at the OTHER
 * measures on the plan.
 *
 * ## Why they are here rather than in the form
 *
 * `validateCompositeForm` in `@repo/ui` decides everything about one measure in isolation. These
 * three are about the graph the measures make together — a cycle, a double count, an input nothing
 * measures — so they need the whole plan, which is this app's business and not that package's.
 * The form takes their verdicts as context.
 *
 * ## They are a front stop, not the authority
 *
 * `statement-file` refuses the whole statement server-side on `cycle`, `double-count` and
 * `input-not-measured`, whether or not this caught them first. What this adds is that the operator
 * meets the rule in the editor, where they can act on it, rather than as a server refusal at Go
 * Live with no indication of which measure caused it. Each is named with the server's own word, so
 * when the two disagree the words say which rule is being argued about.
 *
 * ## One walk, three questions
 *
 * Every rule is reachability over the same edge set: a measure's target points at each of its
 * inputs. The candidate edit replaces whatever the plan holds for its own refid, so the answers
 * describe the plan as it WOULD be rather than as it is.
 *
 * References are the encoded strings `compositeRefOptions` produces. Nothing here decodes one —
 * a node is an opaque key.
 *
 * ## `input-not-measured` is no longer this module's own rule
 *
 * It was, and it answered "is this input measured?" for itself — its own set of other measures'
 * targets, its own observer-count test. That was the third of three different answers to one
 * question (epic `complex-measures-in-configuration`, ledger row 82), and it is now the shared one
 * in `entityCoverage.ts`, which the Observers stage and the Launch checklist read as well.
 *
 * Moving it fixed something on the way past: this module consulted `_deleted` but never
 * `excluded`, so a measure marked *Exclude from processing* — published nowhere, computing
 * nothing — satisfied an input as if it were measuring its target.
 */

/**
 * One measure, as the graph sees it.
 *
 * An alias of `entityCoverage`'s {@link MeasureEdge} rather than a second declaration of the same
 * shape: `findUnmeasuredInputs` below now answers through the shared coverage rule, and two
 * structurally-identical types either side of that call would drift silently the moment one gained
 * a field. The name stays because the three rules here are about the GRAPH the measures make.
 */
export type GraphComposite = MeasureEdge;

/** Edges keyed by target, with the candidate replacing its own entry. */
const buildEdges = (
  others: ReadonlyArray<GraphComposite>,
  candidate: GraphComposite,
): Map<string, string[]> => {
  const edges = new Map<string, string[]>();
  for (const composite of others) {
    if (composite.refid === candidate.refid) continue;
    edges.set(composite.target, composite.inputs.slice());
  }
  // The candidate wins on its own target, even where another measure names the same one — that
  // collision is `duplicate-target`'s to report, and reporting it twice helps nobody.
  edges.set(candidate.target, candidate.inputs.slice());
  return edges;
};

/**
 * Everything reachable from `start` by following edges, `start` excluded unless a cycle returns
 * to it. Iterative rather than recursive: a plan is operator-authored and a cycle must terminate
 * the walk rather than the stack.
 */
const reachableFrom = (start: string, edges: ReadonlyMap<string, string[]>): Set<string> => {
  const seen = new Set<string>();
  const queue = [...(edges.get(start) ?? [])];
  while (queue.length > 0) {
    const node = queue.shift() as string;
    if (seen.has(node)) continue;
    seen.add(node);
    for (const next of edges.get(node) ?? []) queue.push(next);
  }
  return seen;
};

/**
 * `cycle` — a measure that would end up adding up itself, directly or through others.
 *
 * Returns the input the cycle runs through, or `null`. A measure naming its own target as an input
 * is NOT reported here: that is `partial-group-submitted`, the form's rule, and it says something
 * an operator can act on ("tick the box instead") where "this adds up to itself" would not.
 */
export const findCycle = (
  candidate: GraphComposite,
  others: ReadonlyArray<GraphComposite>,
): string | null => {
  const edges = buildEdges(others, candidate);
  for (const input of candidate.inputs) {
    if (input === candidate.target) continue;
    if (reachableFrom(input, edges).has(candidate.target)) return input;
  }
  return null;
};

/**
 * `double-count` — an input that is ALSO reached through another of the same measure's inputs, so
 * the thing would be counted twice in one total.
 *
 * Returns the first pair found: the input that is counted twice, and the one it is reached
 * through. A diamond is the shape this catches — zone A adds up sections 1 and 2, and the whole
 * twin adds up zone A and section 1.
 */
export const findDoubleCounts = (
  candidate: GraphComposite,
  others: ReadonlyArray<GraphComposite>,
): { input: string; through: string } | null => {
  const edges = buildEdges(others, candidate);
  for (const through of candidate.inputs) {
    if (through === candidate.target) continue;
    const beneath = reachableFrom(through, edges);
    for (const input of candidate.inputs) {
      if (input === through) continue;
      if (beneath.has(input)) return { input, through };
    }
  }
  return null;
};

/**
 * `input-not-measured` — an input with no cameras of its own that no other measure totals either,
 * so it would contribute nothing and the total would silently be short.
 *
 * The twin and a floor never have cameras of their own, so for them this reduces to "is it the
 * target of another measure", which is the right question for them too.
 *
 * Answered by the shared coverage rule over the plan as it WOULD be — the candidate replacing
 * whatever the plan holds for its own refid, which is the same substitution `buildEdges` makes for
 * the other three rules.
 *
 * A measure never satisfies its own target: an entity's own observers are what its DERIVED partial
 * group is for, and naming the target as an input is `partial-group-submitted`, the form's rule.
 *
 * ## ⚠ It returns the `no-source` half ONLY, and the propagated case is deliberately left out
 *
 * `completeness-and-coverage` made this rule's shared answer recursive, so a measure now also knows
 * when an input IS totalled but that total is itself unfinished. That case is **not** returned
 * here, because this function's one production caller feeds the editor — and a parent that could
 * not be saved because of a problem two levels down, in a measure the operator is not editing,
 * would be a refusal they cannot act on from where they are standing. The propagated case reaches
 * the stage's list instead, as a warning, read straight from
 * {@link computeCompleteness}.
 */
export const findUnmeasuredInputs = (
  candidate: GraphComposite,
  others: ReadonlyArray<GraphComposite>,
  observerCountsByRef: ReadonlyMap<string, number>,
): string[] => {
  const verdicts = computeCompleteness({
    observerCountsByRef,
    measures: [...others.filter(o => o.refid !== candidate.refid), candidate],
  });

  return (verdicts.get(candidate.refid)?.unsatisfied ?? [])
    .filter(entry => entry.reason === 'no-source')
    .map(entry => entry.ref);
};

/**
 * `duplicate-target` — another measure already totals this thing.
 *
 * After filing, a measure is NAMED for what it totals, so two measures on one target are two rows
 * fighting over one name. This is the same rule the old editor expressed as a unique-name check,
 * in the terms identity actually uses.
 */
export const findDuplicateTarget = (
  candidate: GraphComposite,
  others: ReadonlyArray<GraphComposite>,
  /**
   * Whether two encoded targets are one target. Defaults to the plan encoding; the wizard passes
   * the filing-aware comparison (`compositeVerdicts.ts`), because filing folds some targets the
   * plan encodes apart (`composites-page-launch-refusals`, spec R2).
   */
  sameTarget: (a: string, b: string) => boolean = (a, b) => a === b,
): boolean =>
  others.some(
    composite =>
      composite.refid !== candidate.refid && sameTarget(composite.target, candidate.target),
  );

/**
 * `contained-double-count` — an input that sits PHYSICALLY inside something else the same total
 * already counts, so the thing would be counted twice in one total.
 *
 * The complement of {@link findDoubleCounts} rather than its successor. That rule answers the
 * question the composite graph expresses — "is this input reached through another input's total" —
 * and it is the only thing catching two of the four over-counting pairs on staging plan 51. This
 * one answers the question the graph cannot express at all, because a configuration statement
 * carries observers and totals and no hierarchy: a zone that sits inside an area with no total
 * linking them.
 *
 * ## Composite edges first, then ONE containment step — never the reverse
 *
 * The walk starts at each input and follows the **composite** edges, exactly as `findDoubleCounts`
 * does, and then asks whether any node it reached physically contains another of the same measure's
 * inputs. That catches the mixed case the spec names: a total adding up `X` and `B`, where `X` is a
 * composite over `A` and `B` sits inside `A` — invisible to both of today's rules.
 *
 * ⚠ **A containment edge is deliberately NOT composed with a composite edge after it**, and that is
 * a correctness bound rather than an optimisation. A space's cameras see what is physically inside
 * it; they do not see whatever a composite beneath it happens to total. So "A contains D, and D
 * totals B" does **not** mean A's number includes B — B may be nowhere near A — and a walk over a
 * genuine union edge set would refuse a correct measure on exactly that shape. Containment itself is
 * transitive (a nested polygon's centroid lies inside its container's), so one step is all it needs.
 *
 * ## It never re-reports a pair {@link findDoubleCounts} reports
 *
 * A pair reachable through the composite edges alone belongs to that rule and keeps its own
 * sentence. But this rule still RUNS on a measure that already has one: the whole-twin total on the
 * rig carries both kinds and says both, on two different rows. Rob's ruling, 2026-09-19 — an
 * earlier draft gated this on `findDoubleCounts` finding nothing, which would have left the biggest
 * total on the gym silent about half of what is wrong with it.
 *
 * ## `findCycle` keeps the composite-only edge set, and that is deliberate
 *
 * A total whose target sits inside one of its inputs is wrong in a different way, and calling it a
 * cycle would put a misleading sentence on it.
 */
export interface ContainedDoubleCount {
  /** The input that would be counted twice. */
  input: string;
  /** The thing it sits inside — a watched space, which may be an input or something beneath one. */
  through: string;
}

/** A pair the plan could not decide. `because` is a discriminator; `@repo/ui` writes the sentence. */
export interface UndecidedContainment {
  input: string;
  through: string;
  because: UndecidedReason;
}

export interface ContainmentVerdicts {
  contained?: ContainedDoubleCount;
  undecided?: UndecidedContainment;
}

export const findContainedDoubleCounts = (
  candidate: GraphComposite,
  others: ReadonlyArray<GraphComposite>,
  containment: PlanContainment,
): ContainmentVerdicts => {
  const edges = buildEdges(others, candidate);
  // Naming the target among its own inputs is `partial-group-submitted`, the form's rule.
  const inputs = candidate.inputs.filter(input => input !== candidate.target);

  /*
   * What each input already counts through the COMPOSITE edges — the same walk `findDoubleCounts`
   * makes, over the same edge set, so the two rules divide the pairs between them by construction
   * rather than by agreement.
   */
  const reach = new Map<string, Set<string>>();
  for (const input of inputs) {
    if (!reach.has(input)) reach.set(input, reachableFrom(input, edges));
  }

  /*
   * The ONE pair `findDoubleCounts` actually reports keeps its own sentence here.
   *
   * ⚠ **The PAIR, not the input — and the difference is a refusal nothing else would report.**
   * This has been narrowed twice. It began by skipping every input reachable from another input,
   * which left the inputs that rule did not name with no sentence from either rule. Narrowing it to
   * the reported INPUT fixed that but was still too broad: an input reported as counted through `A`
   * was then never asked about any other container, so an input sitting inside a second watched
   * space `C` had that fault swallowed too. Only the exact `{input, through}` pair is the same fact
   * said twice; every other pairing is a separate fault. Found by review (the panel, then Copilot),
   * 2026-09-19.
   *
   * Suppressing even that pair is belt-and-braces rather than load-bearing: `compositeRules.ts`
   * writes `doubleCount` before `containedInput` and both use `errors.rows[i] ??= message`, so on a
   * shared row the composite sentence already wins. What this guarantees is that the two rules
   * never describe the same pair in two different sentences.
   */
  const reported = findDoubleCounts(candidate, others);
  const alreadySaid = (input: string, container: string) =>
    reported !== null && reported.input === input && reported.through === container;

  /*
   * Everything each container already counts: itself, plus whatever its own composite chain
   * reaches. Built ONCE per container — it does not vary with the input being tested, and
   * rebuilding it inside the input loop was the same work n times for every one of n containers.
   */
  const countedFor = new Map<string, string[]>();
  for (const through of inputs) {
    const counted: string[] = [through];
    // `.forEach` rather than spreading the Set: this app compiles to ES5 without
    // `downlevelIteration`, where iterating a Set directly is a compile error.
    (reach.get(through) ?? new Set<string>()).forEach(node => counted.push(node));
    countedFor.set(through, counted);
  }

  let contained: ContainedDoubleCount | undefined;
  let undecided: UndecidedContainment | undefined;

  for (const input of inputs) {
    let hit: string | undefined;
    let maybe: UndecidedContainment | undefined;

    for (const through of inputs) {
      if (hit) break;
      if (through === input) continue;

      for (const node of countedFor.get(through) ?? []) {
        if (node === input) continue;
        // The one pair the composite rule already names is skipped; every other container this
        // input sits inside is a separate fault and is still asked about.
        if (alreadySaid(input, node)) continue;
        const answer = containment.contains(node, input);
        if (answer === true) {
          hit = node;
          break;
        }
        /*
         * ⚠ **An undecided pair is only worth saying where the operator can SEE both halves.**
         * `node` may be something reached through the composite chain that this measure never
         * names — and neither label map is built to hold such a reference, so the message would
         * render the raw encoded value, U+001F separator and all, naming an entity the operator
         * cannot find on the measure or on the plan. Worse, it would block Save on a fix they
         * cannot make. A pair they can act on is one whose container is one of their own inputs.
         * The `contained` verdict needs no such guard: its container must be both watched and
         * live, so it is always in the options. Found by review, 2026-09-19.
         */
        if (answer === 'unknown' && !maybe && node === through) {
          const because = containment.whyUnknown(node, input);
          if (because) maybe = { input, through: node, because };
        }
      }
    }

    /*
     * A definite answer for this input suppresses its own undecided one, across EVERY container:
     * a pair one container could not place is not worth asking a person about where another
     * container settles it. Both verdicts may still be present on one measure — for two different
     * inputs, landing on two different rows.
     */
    if (hit) {
      if (!contained) contained = { input, through: hit };
    } else if (maybe && !undecided) {
      undecided = maybe;
    }
  }

  return {
    ...(contained ? { contained } : {}),
    ...(undecided ? { undecided } : {}),
  };
};
